import React, { useState, useEffect, useCallback } from 'react';
import Board from './Board';
import NextPiece from './NextPiece';
import { randomTetromino } from './tetrominoes';
import {
  createBoard,
  checkCollision,
  rotate,
  mergeTetromino,
  clearLines,
  renderBoard
} from './gameHelpers';

function Game() {
  const [board, setBoard] = useState(createBoard());
  const [nextTetromino, setNextTetromino] = useState(randomTetromino());
  const [player, setPlayer] = useState({
    pos: { x: 4, y: 0 },
    tetromino: randomTetromino()
  });
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // 플레이어 이동
  const movePlayer = useCallback((dir) => {
    if (gameOver || isPaused) return;

    if (!checkCollision(player, board, { x: dir, y: 0 })) {
      setPlayer(prev => ({
        ...prev,
        pos: { x: prev.pos.x + dir, y: prev.pos.y }
      }));
    }
  }, [player, board, gameOver, isPaused]);

  // 플레이어 회전
  const rotatePlayer = useCallback(() => {
    if (gameOver || isPaused) return;

    const rotatedTetromino = {
      ...player.tetromino,
      shape: rotate(player.tetromino.shape)
    };

    const pos = player.pos.x;
    let offset = 1;

    while (checkCollision({ ...player, tetromino: rotatedTetromino }, board, { x: 0, y: 0 })) {
      player.pos.x += offset;
      offset = -(offset + (offset > 0 ? 1 : -1));
      if (offset > rotatedTetromino.shape[0].length) {
        player.pos.x = pos;
        return;
      }
    }

    setPlayer(prev => ({
      ...prev,
      tetromino: rotatedTetromino
    }));
  }, [player, board, gameOver, isPaused]);

  // 플레이어 하강
  const drop = useCallback(() => {
    if (gameOver || isPaused) return;

    if (!checkCollision(player, board, { x: 0, y: 1 })) {
      setPlayer(prev => ({
        ...prev,
        pos: { x: prev.pos.x, y: prev.pos.y + 1 }
      }));
    } else {
      // 보드에 테트로미노 고정
      const newBoard = mergeTetromino(board, player);
      const { board: clearedBoard, linesCleared } = clearLines(newBoard);

      setBoard(clearedBoard);

      // 라인 클리어에 따른 점수 계산
      let points = 0;
      if (linesCleared === 1) points = 100;
      else if (linesCleared === 2) points = 200;
      else if (linesCleared === 3) points = 500;
      else if (linesCleared === 4) points = 800;

      setScore(prev => prev + points);

      // 새로운 테트로미노 생성
      const newPlayer = {
        pos: { x: 4, y: 0 },
        tetromino: nextTetromino
      };

      if (checkCollision(newPlayer, clearedBoard, { x: 0, y: 0 })) {
        setGameOver(true);
      } else {
        setPlayer(newPlayer);
        setNextTetromino(randomTetromino());
      }
    }
  }, [player, board, nextTetromino, gameOver, isPaused]);

  // 빠른 하강
  const hardDrop = useCallback(() => {
    if (gameOver || isPaused) return;

    let dropDistance = 0;
    while (!checkCollision(player, board, { x: 0, y: dropDistance + 1 })) {
      dropDistance++;
    }

    const droppedPlayer = {
      ...player,
      pos: { x: player.pos.x, y: player.pos.y + dropDistance }
    };

    // 보드에 테트로미노 고정
    const newBoard = mergeTetromino(board, droppedPlayer);
    const { board: clearedBoard, linesCleared } = clearLines(newBoard);

    setBoard(clearedBoard);

    // 라인 클리어에 따른 점수 계산
    let points = 0;
    if (linesCleared === 1) points = 100;
    else if (linesCleared === 2) points = 200;
    else if (linesCleared === 3) points = 500;
    else if (linesCleared === 4) points = 800;

    setScore(prev => prev + points);

    // 새로운 테트로미노 생성
    const newPlayer = {
      pos: { x: 4, y: 0 },
      tetromino: nextTetromino
    };

    if (checkCollision(newPlayer, clearedBoard, { x: 0, y: 0 })) {
      setGameOver(true);
    } else {
      setPlayer(newPlayer);
      setNextTetromino(randomTetromino());
    }
  }, [player, board, nextTetromino, gameOver, isPaused]);

  // 키보드 제어
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (gameOver) return;

      // 게임 키 입력시 기본 동작 방지
      if (['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp', ' '].includes(e.key)) {
        e.preventDefault();
      }

      switch (e.key) {
        case 'ArrowLeft':
          movePlayer(-1);
          break;
        case 'ArrowRight':
          movePlayer(1);
          break;
        case 'ArrowDown':
          drop();
          break;
        case 'ArrowUp':
          rotatePlayer();
          break;
        case ' ':
          hardDrop();
          break;
        case 'p':
        case 'P':
          setIsPaused(prev => !prev);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [movePlayer, drop, rotatePlayer, hardDrop, gameOver]);

  // 자동 하강 (점수에 따라 속도 증가)
  useEffect(() => {
    if (gameOver || isPaused) return;

    // 점수가 500점씩 오를 때마다 속도 증가 (최소 100ms)
    const level = Math.floor(score / 500);
    const speed = Math.max(100, 1000 - level * 100);

    const interval = setInterval(() => {
      drop();
    }, speed);

    return () => clearInterval(interval);
  }, [drop, gameOver, isPaused, score]);

  // 게임 재시작
  const resetGame = () => {
    setBoard(createBoard());
    setPlayer({
      pos: { x: 4, y: 0 },
      tetromino: randomTetromino()
    });
    setNextTetromino(randomTetromino());
    setScore(0);
    setGameOver(false);
    setIsPaused(false);
  };

  const displayBoard = renderBoard(board, player);
  const currentLevel = Math.floor(score / 500) + 1;

  return (
    <div className="game-container">
      <h1>테트리스</h1>
      <div className="game-info">
        <div className="score">점수: {score}</div>
        <div className="level">레벨: {currentLevel}</div>
        {isPaused && <div className="status">일시정지</div>}
        {gameOver && <div className="status game-over">게임 오버!</div>}
      </div>
      <div className="game-layout">
        <Board board={displayBoard} />
        <NextPiece tetromino={nextTetromino} />
      </div>
      <div className="controls">
        <p>← → : 이동</p>
        <p>↑ : 회전</p>
        <p>↓ : 빠르게 내리기</p>
        <p>Space : 즉시 떨어뜨리기</p>
        <p>P : 일시정지</p>
      </div>
      {gameOver && (
        <button className="restart-button" onClick={resetGame}>
          다시 시작
        </button>
      )}
    </div>
  );
}

export default Game;
